import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RefreshCw } from "lucide-react";

/**
 * SyncStatusPill — shows "Last sync X ago" for the most-recently-updated
 * operational collection (orders / batches / stock / purchases). Auto-refreshes
 * every 30s. Click to force-refresh.
 * Renders inline in the top-nav; auto-hides if no data.
 */
export default function SyncStatusPill() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/insights/sync-status");
      setData(r.data);
    } catch (_e) { /* ignore */ }
    finally { setBusy(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;
  const stamps = ["orders", "batches", "stock", "purchases"]
    .map((k) => data[k]).filter(Boolean).sort().reverse();
  const latest = stamps[0];
  if (!latest) return null;

  const ago = timeAgo(latest);
  const isFresh = ago.minutes < 10;

  return (
    <button
      type="button"
      onClick={load}
      disabled={busy}
      title={`Latest activity: ${new Date(latest).toLocaleString()}`}
      className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full border ${isFresh ? "bg-[#EAF5E9] border-[#B7D5B4] text-[#3D5A3A]" : "bg-[#FFF5F0] border-[#F0D0C0] text-[#7A2E2E]"} transition`}
      data-testid="sync-status-pill"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isFresh ? "bg-[#4A6E46]" : "bg-[#B87B41]"} ${busy ? "animate-pulse" : ""}`} />
      Last activity {ago.text}
      <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
    </button>
  );
}

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return { text: `${Math.round(s)}s ago`, minutes: 0 };
  const m = s / 60;
  if (m < 60) return { text: `${Math.round(m)} min ago`, minutes: m };
  const h = m / 60;
  if (h < 24) return { text: `${Math.round(h)}h ago`, minutes: m };
  return { text: `${Math.round(h / 24)}d ago`, minutes: m };
}
